



















































import Vue from 'vue';
import moment from 'moment';
import { getMessageById } from '@/utils/message';

/**
 * @module common/CtrlDatePicker
 * @desc 日付入力コンポーネント。テキストフィールドコンポーネントであり、DatePickerでの入力または直接入力ができる。
 * @desc アクティブ時はyyyyMMdd形式、非アクティブ時はyyyy-MM-dd形式で表示される。
 * @vue-data {String} [当日日付(yyyy-MM-dd)] date -- datePickerで参照する日付
 * @vue-data {String} [当日日付(yyyy-MM-dd)] displayDate -- テキストフィールドで参照する日付
 * @vue-data {boolean} [false] menu -- datePickerの表示/非表示フラグ
 * @vue-watch {String} date -- テキストが変更された場合datePickerの日付を変更する
 * @vue-event {String} numDate -- yyyy-MM-dd形式をyyyyMMdd形式に変換する
 * @vue-event {String} hyphenDate - yyyyMMdd形式をyyyy-MM-dd形式に変換する
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default Vue.extend<any, any, any, any>({
  name: 'CtrlDatePicker',
  props: {
    // property [label] を追加する(現在は "日付" と固定表示されている)
    label: {
      type: String,
      default: '日付',
    },
    // property [required] を追加する("true"の場合 ①必須 check する ②[label]の値の前に"*"を付ける)
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    offset: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: '115px',
    },
    value: {
      type: [Number, String],
    },
  },
  data() {
    return {
      displayDate: '',
      beforeDate: '',
      menu: false,
    };
  },

  methods: {
    // フォーカス解除時、yyyy-MM-dd 形式に変更する。また、フォーカス時は yyyyMMdd 形式に変更する
    /**
     * yyyy-MM-dd形式をyyyyMMdd形式に変換する
     * @param {String} date -- yyyy-MM-dd形式の日付文字列
     */
    numDate(date: string) {
      if (!date) return '';
      if (date.indexOf('-') < 0) return date;
      const [year, month, day] = date.split('-');
      return `${year}${month}${day}`;
    },
    /**
     * yyyyMMdd形式をyyyy-MM-dd形式に変換する
     * @param {String} date -- yyyyMMdd形式の日付文字列
     */
    hyphenDate(date: string | number) {
      if (!date) return '';
      if (typeof date === 'number') {
        date = date.toString();
      }
      if (date.indexOf('-') >= 0) return date;
      if (date.length === 4) {
        // 4桁入力して onBlur event が発生した場合、その接頭に当年(例: 2022) を付けて日付チェックを行う
        let y = new Date().getFullYear();
        let m = date.substr(0, 2);
        let d = date.substr(2, 4);
        return `${y}-${m}-${d}`;
      } else if (date.length === 6) {
        let y = new Date().getFullYear().toString().substr(0, 2) + date.substr(0, 2);
        let m = date.substr(2, 2);
        let d = date.substr(4, 2);
        return `${y}-${m}-${d}`;
      } else if (date.length === 8) {
        let y = date.substr(0, 4);
        let m = date.substr(4, 2);
        let d = date.substr(6, 2);
        return `${y}-${m}-${d}`;
      } else {
        return date;
      }
    },
    handleChangeDate(val: string) {
      this.menu = false;
      // Convert the string to a Date object
      const date = new Date(val);
      // Get the year from the Date object
      const year = date.getFullYear();
          // Check if the year is less than 2020 or greater than 2099
      if (year < 2020 || year > 2099) {
        this.$emit('input', '');
        this.$emit('change', '');
        this.displayDate = '';
      } else {
        this.$emit('input', val);
        this.$emit('change', val);
        this.displayDate = val;
      }
    },
    handleOnInput(val: string) {
      return val.replace(/\D/g, '');
    },
    handleOnBlur() {
      if (!this.displayDate || this.displayDate === '') {
        this.$emit('input', null);
        return;
      }
      let date = this.displayDate.replace(/\D/g, '');
      if (moment(date, ['YYYY-MM-DD', 'YYYYMMDD', 'YYMMDD', 'MMDD'], true).isValid()) {
        const dataInput = +moment(date, ['YYYY-MM-DD', 'YYYYMMDD', 'YYMMDD', 'MMDD'], true).format('YYYYMMDD');
        if(dataInput < 20200101 || dataInput > 20991231) date = '';
        this.displayDate = this.hyphenDate(date);
        this.$emit('input', this.hyphenDate(date));
        if (!this.menu && this.beforeDate !== this.displayDate) {
          this.$emit('change', this.hyphenDate(date));
        }
      } else {
        this.displayDate = '';
        this.$emit('input', null);
      }
    },
    handleOnFocus(focusEvent: any) {
      if (this.readonly) return;
      this.beforeDate = this.displayDate;
      this.displayDate = this.numDate(this.displayDate);
      this.$nextTick(function () {
        focusEvent.target.select();
      });
    },
  },
  computed: {
    computedWidth() {
      return `max-width: ${this.maxWidth}`;
    },
    computedDate() {
      return this.hyphenDate(this.value);
    },
    computedReadOnly() {
      return this.menu ? true : this.readonly;
    },
    isValid() {
      return (value: string) => {
        // 日付の直接入力は「yyyyMMdd」または「MMdd」形式のみとする
        const formats = ['YYYY-MM-DD', 'YYYYMMDD', 'YYMMDD', 'MMDD'];
        // （十時作業）入力中のformatにMMDD形式を追加する
        if (value) {
          return (
            moment(value, formats, true).isValid() || getMessageById('KI_W009')
          );
        } else {
          // property [required] を追加する("true"の場合 ①必須 check する ②[label]の値の前に"*"を付ける→   [rules] で対応を想定
          if (this.required) {
            return getMessageById('KI_W007');
          }
          return true;
        }
      };
    },
  },
  watch: {
    value(newVal) {
      this.displayDate = this.hyphenDate(newVal);
    },
  },
  mounted() {
    // 画面描画時に 使用画面の設定値を初期表示する
    this.displayDate = this.hyphenDate(this.value);
  },
});
